import { useState, useEffect } from "react";
import AddBankAccount from "./AddBankAccount";
import api from "../../services/api";

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);


const InfoBox = ({ label, value, onCopy, onToggle, masked, showToggle }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (<>
    <div className="bg-[#F3F4F5] rounded-[16px] p-[16px]">
      <p className="text-[10px] font-semibold text-[#4C444E] uppercase tracking-wide mb-1.5">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[16px] font-bold text-[#191C1D] tracking-wide">
          {masked ? "••••••••" + value.slice(-4) : value}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {showToggle && (
            <button onClick={onToggle} className="p-0 border-none bg-transparent cursor-pointer leading-none hover:opacity-70">
              {masked ? <EyeIcon /> : <EyeOffIcon />}
            </button>
          )}
          <div className="relative">
            <button onClick={handleCopy} className="p-0 border-none bg-transparent cursor-pointer leading-none hover:opacity-70">
              <CopyIcon />
            </button>
            {copied && (
              <span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[9px] px-2 py-0.5 rounded whitespace-nowrap">
                Copied!
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  </>);
};

const BankCard = ({ bank }) => {
  const [masked, setMasked] = useState(true);

  return (
    <div className="bg-[#FFFFFF] rounded-[32px] py-[32px] px-[18px] w-70  shadow-[0px_12px_24px_0px_#4B23640A]">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[20px] font-bold text-[#191C1D]">{bank.name}</span>
        <span className={`text-[10px] font-bold uppercase tracking-wide px-[12px] py-[4px] rounded-full border ${
          bank.status?.toLowerCase() === "approved"
            ? "bg-[#DCFCE7] border-[#BBF7D0] text-[#15803D]"
            : bank.status?.toLowerCase() === "pending"
            ? "bg-amber-50 border-amber-200 text-amber-700"
            : "bg-red-50 border-red-200 text-red-600"
        }`}>
          {bank.status}
        </span>
      </div>

      <div className="space-y-2.5">
        <InfoBox
          label="Account Number"
          value={bank.accountNumber}
          masked={masked}
          showToggle
          onToggle={() => setMasked(!masked)}
        />
        <InfoBox
          label="IFSC Code"
          value={bank.ifsc}
          masked={false}
          showToggle={false}
        />
      </div>
    </div>
  );
};

const filters = ["All", "Approved", "Pending", "Rejected"];

export default function WhitelistedBankAccounts() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [isAddBankOpen, setIsAddBankOpen] = useState(false);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBanks = async () => {
    try {
      const res = await api.get('/bank-account/status');
      const data = res.data?.data;
      if (data) {
         const arr = Array.isArray(data) ? data : [data];
         const mapped = arr.map(item => ({
            id: item.bank_account_id,
            name: item.bank_name,
            status: item.status,
            accountNumber: item.account_number,
            ifsc: item.ifsc_code
         }));
         setBanks(mapped);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const filteredBanks = banks.filter((b) => {
    if (activeFilter === "All") return true;
    return b.status?.toLowerCase() === activeFilter.toLowerCase();
  });

  return (
    <>
        <div className="flex-1 overflow-y-auto px-8 py-7 bg-[#F8F9FA]">
          <h1 className="text-[36px] font-bold text-[#34094D] mb-1">Whitelisted Bank Accounts</h1>
          <p className="text-[18px] text-[#4C444E]">Manage institutional liquidity bridges and secure destination</p>
          <div className="p-[28px]">
            {/* Filter Tabs */}
            <div className="inline-flex gap-0.5 bg-[#F3F4F5] w-[620px] rounded-xl p-1 mb-6">
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-4 py-1.5 rounded-lg text-[12px] font-semibold border-none cursor-pointer transition-all ${
                    activeFilter === f
                      ? "bg-white text-gray-900 shadow-sm"
                      : "bg-transparent text-gray-400 hover:text-gray-600"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Cards */}
            {loading ? (
              <div className="flex items-center justify-center p-10">
                <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3.5 items-start">
                {filteredBanks.map((bank) => (
                  <BankCard key={bank.id} bank={bank} />
                ))}

                {/* Request New Bank */}
              <div 
                className="border-2 bg-[linear-gradient(135deg,#FFFFFF_0%,#F3F4F5_100%)] border-[#EA85344D] border-dashed rounded-2xl px-[32px] py-[56px] w-60 min-h-40 flex flex-col items-center justify-center gap-2.5 cursor-pointer hover:bg-orange-50 transition-colors"
                onClick={() => setIsAddBankOpen(true)}
              >
                <div className="w-11 h-11 rounded-full bg-[linear-gradient(135deg,#EA8534_0%,#A0111A_100%)] flex items-center justify-center flex-shrink-0">
                  <PlusIcon />
                </div>
                <div className="text-center">
                  <p className="text-[18px] font-bold text-[#4B2364] mb-1">Request New Bank</p>
                  <p className="text-[14px] font-medium text-[#4C444E] leading-relaxed">
                    Enable a new virtual account partner for your business.
                  </p>
                </div>
                </div>
              </div>
            )}
          </div>
        </div>
      
      {isAddBankOpen && (
        <AddBankAccount 
          onClose={() => setIsAddBankOpen(false)} 
          onSuccess={() => {
            setIsAddBankOpen(false);
            fetchBanks();
          }} 
        />
      )}
    </>
  );
}
